import { Box } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';

const ProgressiveBg = ({
  backgroundImage,
  lazyLoad = false,
  display = 'block',
  style = {},
  ...rest
}) => {
  const [loadedSources, setLoadedSources] = useState({});
  const [isInView, setIsInView] = useState(!lazyLoad);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazyLoad) {
      setIsInView(true);
      return;
    }

    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Load images when component comes into view
  useEffect(() => {
    if (!isInView) return;

    const entries = Object.entries(backgroundImage);
    const loadImages = async () => {
      const promises = entries
        .filter(([_, src]) => src && src !== 'none') // Skip "none" and falsy values
        .map(([key, src]) => {
          const img = new Image();
          img.src = src;
          return new Promise(resolve => {
            img.onload = () => resolve([key, src]);
            img.onerror = () => resolve([key, 'error']); // Optional: mark errors
          });
        });

      const results = await Promise.all(promises);

      // Include skipped "none" values in the final result
      const skipped = entries
        .filter(([_, src]) => !src || src === 'none')
        .map(([key, src]) => [key, src]);

      const loadedMap = Object.fromEntries([...results, ...skipped]);

      setLoadedSources(loadedMap);
    };

    loadImages();
  }, [backgroundImage, isInView]);

  return (
    <div ref={containerRef}>
      {Object.entries(backgroundImage).map(([bp, src]) => {
        const displayObj = {
          base: 'none',
          sm: 'none',
          md: 'none',
          lg: 'none',
          xl: 'none',
          '2xl': 'none',
          [bp]: display,
        };

        const blurSrc = src === 'none' ? src : `${src}?blur=40`;
        const loadedSrc = loadedSources[bp];
        const isNotInView = lazyLoad && !isInView;

        return (
          <Box
            key={bp}
            display={displayObj}
            {...rest}
            backgroundImage={
              src === 'none'
                ? src
                : isNotInView
                ? blurSrc
                : `url(${loadedSrc || blurSrc})`
            }
            style={{
              ...style,
              filter: lazyLoad
                ? isNotInView
                  ? 'blur(10px)'
                  : loadedSrc
                  ? 'blur(0px)'
                  : 'blur(10px)'
                : 'none',
              transition: 'filter 0.5s ease',
            }}
          />
        );
      })}
    </div>
  );
};

export default ProgressiveBg;
